<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="consent-container">
    <h2>E-Sign Act Disclosure and Agreement</h2>
    <strong>Your Consent To Use Electronic Records and Signatures</strong>
    <p>
      From time to time,
      <strong>{{ entity.name }}</strong> and its affiliates or agents (collectively, “we”, “us” or
      “Company”) may provide you with certain notices, disclosures or contracts, some of which are
      required by law to be “in writing”. The federal E-SIGN Act and certain state laws allow us to
      provide you these written notices, disclosures or contracts electronically with your consent.
      These laws also allow the use of electronic signatures. Described below are the terms and
      conditions for providing you such notices, disclosures or contracts electronically as well as
      to use electronic signatures. If you can access this information electronically to your
      satisfaction and agree to these terms and conditions, please confirm your acceptance of this
      agreement by clicking the “Sign” button.. If you do not agree to use electronic signatures or
      to receive Electronic Records, click the link indicating you reject the document.
    </p>
    <strong>Right To Have Records Provided On Paper</strong>
    <p>
      At any time, you may request delivery of paper copies of any record provided electronically by
      contacting us. We may charge a fee for each paper statement copy you request. Additionally,
      following your signing session, you will receive a signed copy to the provided email address
      and you will have the ability to download and print your signed document through the GetAccept
      system for a limited period (usally 30 days).
    </p>
    <strong>Right To Withdraw Your Consent To Receive Electronic Records; Consequences</strong>
    <p>
      If you agree to receive Electronic Records, you have the right to withdraw your consent at any
      time and at no cost to you. You must inform us of your decision by contacting us by submitting
      a request to {{ senderEmail }} or the address provided on our webpage. In the message you must
      provide us your:
    </p>
    <ul>
      <li>Email address that has been used</li>
      <li>Full name</li>
      <li>Postal address</li>
      <li>Telephone number</li>
    </ul>
    <p>
      If you elect to receive records only in paper format, or refuse to sign electronically, it may
      slow down the speed at which you receive documents or information about your account or
      services.
    </p>

    <strong>Your Contact Information</strong>
    <p>
      If you elect to receive Electronic Notices, you must keep your email address ,which was
      provide us at the time of signature, up to date. You may change that email address at any time
      by contacting us at
      <strong>{{ senderEmail }}</strong
      >. Provide us your previous email address and your new one in the body of the email.
    </p>
    <strong>Hardware And Software Minimum Requirements</strong>
    <table class="hardware-table">
      <tbody>
        <tr>
          <td nowrap>Operating Systems:</td>
          <td>Windows Vista; Windows 7; Mac OS® X</td>
        </tr>
        <tr>
          <td>Browsers:</td>
          <td>
            Final release versions of Internet Explorer 10.0 or above (Windows only); Mozilla
            Firefox 3.0 or above (Windows and Mac); Safari 3.0 or above (Mac OS only); Google Chrome
            5.0 or above.
          </td>
        </tr>
        <tr>
          <td>PDF Reader:</td>
          <td>
            Current version of Acrobat® or similar software that allows you to receive PDF files.
          </td>
        </tr>
        <tr>
          <td>Screen Resolution:</td>
          <td>800 x 600 minimum</td>
        </tr>
        <tr>
          <td>Enabled Security Settings:</td>
          <td>Allow per session cookies</td>
        </tr>
        <tr>
          <td>Hardware:</td>
          <td>
            A personal computer, tablet or smartphone on which you have access to the Internet
          </td>
        </tr>
      </tbody>
    </table>
    <p>
      Pre-release (e.g. beta) versions of operating systems and browsers are not supported. You must
      also have an active email address. These minimum requirements are subject to change in our
      sole discretion. If these requirements change, and such change creates a material risk that
      you would not be able to access or retain your Electronic Records, we will provide you notice
      at the email address we have on file for you, at which time you will have the right to
      withdraw your consent.
    </p>

    <strong>Acknowledgement Of Your Access And Consent To Electronic Records</strong>
    <p>By consenting to use electronic records and signatures, you agree that</p>
    <ul>
      <li>You were able to read this electronic E-SIGN ACT DISCLOSURE AND AGREEMENT</li>
      <li>
        You have an active email address on file with Company and confirm that you have the hardware
        and/or software described above.
      </li>
      <li>
        Until you notify Company as described above, you consent to receive electronically any
        records, notices, agreements or disclosures made to me by Company during the course of your
        relationship with Company.
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import type { Entity } from '@getaccept/lib-shared/src/entity/signing-site/types/entity';
import type { SigningPageUser } from '@getaccept/lib-shared/src/types/signing-page-user';
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';

export default defineComponent({
  props: {
    entity: {
      type: Object as PropType<Entity>,
      required: true,
    },
    sender: {
      type: Object as PropType<SigningPageUser>,
      required: true,
    },
  },
  setup(props) {
    const senderEmail = computed(() => props.entity.email || props.sender.email);

    return {
      senderEmail,
    };
  },
});
</script>

<style lang="scss" scoped>
.hardware-table {
  border: 1px;
  width: 100%;
  border-collapse: collapse;

  td {
    padding: 5px;
    margin: 0;
  }
}

.consent-container {
  max-height: 300px;
  border: 1px solid var(--gray-89);
  padding: var(--spacing-100);
  overflow-y: auto;
}
</style>
